import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-hot-toast";
import { LuBuilding2, LuCheck, LuChevronsUpDown, LuUndo2 } from "react-icons/lu";
import API from "../../api";
import { capture } from "../../instrument";
import { getSetting } from "../../queries/setting";
import { FIELD_STATE } from "../../utils";

const FIELD_GROUPS = [
  {
    label: "Structure",
    blocks: [
      {
        category: "admission",
        fields: [
          { key: "establishment", label: "Établissement" },
          { key: "silReservationNumber", label: "N° de réservation" },
          { key: "silAdmissionNumber", label: "N° dossier SIL" },
        ],
      },
    ],
  },
  {
    label: "Patient",
    blocks: [
      {
        label: "Identité",
        category: "patient",
        fields: [
          { key: "firstName", label: "Prénom" },
          { key: "lastName", label: "Nom" },
          { key: "birthLastName", label: "Nom de naissance" },
          { key: "sexe", label: "Sexe" },
          { key: "birthdate", label: "Date de naissance" },
          { key: "birthplaceCode", label: "Lieu de naissance" },
          { key: "INS", label: "INS" },
        ],
      },
      {
        label: "Séjour",
        category: "patient",
        fields: [
          { key: "IPP", label: "IPP" },
          { key: "admissionId", label: "Numéro d'admission" },
          { key: "hostService", label: "Service" },
          { key: "room", label: "Chambre" },
        ],
      },
      {
        label: "Contact",
        category: "patient",
        fields: [
          { key: "phone", label: "Téléphone" },
          { key: "email", label: "Email" },
          { key: "address", label: "Adresse" },
          { key: "zipCode", label: "Code postal", isPartOfAddress: true },
          { key: "city", label: "Ville", isPartOfAddress: true },
        ],
      },
      {
        label: "Morphologie",
        category: "patient",
        fields: [
          { key: "weight", label: "Poids" },
          { key: "height", label: "Taille" },
        ],
      },
    ],
  },
  {
    label: "Mutuelle",
    blocks: [
      {
        category: "mutuelle",
        fields: [
          { key: "code", label: "Code mutuelle" },
          { key: "adherentNumber", label: "N° adhérent" },
          { key: "rightsFrom", label: "Droits du" },
          { key: "rightsTo", label: "Droits au" },
        ],
      },
    ],
  },
  {
    label: "Prélèvement",
    blocks: [
      {
        category: "admission",
        fields: [
          { key: "samplingDate", label: "Date et heure" },
          { key: "samplerCode", label: "Préleveur" },
        ],
      },
    ],
  },
  {
    label: "Prescription",
    blocks: [
      {
        category: "prescription",
        fields: [
          { key: "date", label: "Date" },
          { key: "doctor", label: "Médecin" },
          { key: "silPrescriptionNumber", label: "ID externe" },
        ],
      },
    ],
  },
];

const FIELD_STATES = [
  { value: FIELD_STATE.DISABLE, label: "Désactivé" },
  { value: FIELD_STATE.OPTIONAL, label: "Optionnel" },
  { value: FIELD_STATE.REQUIRE_FORCEABLE, label: "Requis forçable" },
  { value: FIELD_STATE.REQUIRE, label: "Requis" },
];

const countOverrides = (laboratory) => Object.values(laboratory.activeFieldsOverrides || {}).reduce((count, fields) => count + Object.keys(fields).length, 0);

const VisibleFieldsSection = () => {
  const queryClient = useQueryClient();
  const [scopeLaboratoryId, setScopeLaboratoryId] = useState("");

  const { data: setting } = useQuery({ queryKey: ["setting"], queryFn: getSetting });
  const { data: laboratories = [] } = useQuery({
    queryKey: ["laboratories"],
    queryFn: async () => {
      const { ok, data, message } = await API.get("/admin/laboratory");
      if (!ok) throw new Error(message);
      return data;
    },
  });

  const scopeLaboratory = laboratories.find((laboratory) => laboratory._id === scopeLaboratoryId);
  const overrides = scopeLaboratory?.activeFieldsOverrides || {};
  const overridesCount = scopeLaboratory ? countOverrides(scopeLaboratory) : 0;

  const activeFieldsMutation = useMutation({
    mutationKey: ["setting", "activeFields"],
    mutationFn: (activeFields) => API.post("/setting", { activeFields }),
    onMutate: async (activeFields) => {
      await queryClient.cancelQueries({ queryKey: ["setting"] });
      queryClient.setQueryData(["setting"], (cached) => ({ ...cached, activeFields }));
    },
    onError: (error) => {
      capture(error);
      toast.error("Erreur d'enregistrement des champs visibles");
    },
    onSettled: () => {
      if (queryClient.isMutating({ mutationKey: ["setting", "activeFields"] }) === 1) queryClient.invalidateQueries({ queryKey: ["setting"] });
    },
  });

  const overridesMutation = useMutation({
    mutationKey: ["laboratories", "activeFieldsOverrides"],
    mutationFn: ({ laboratoryId, activeFieldsOverrides }) => API.put(`/admin/laboratory/${laboratoryId}`, { activeFieldsOverrides }),
    onMutate: async ({ laboratoryId, activeFieldsOverrides }) => {
      await queryClient.cancelQueries({ queryKey: ["laboratories"] });
      queryClient.setQueryData(["laboratories"], (cached) => cached.map((laboratory) => (laboratory._id === laboratoryId ? { ...laboratory, activeFieldsOverrides } : laboratory)));
    },
    onError: (error) => {
      capture(error);
      toast.error(error.message || "Erreur d'enregistrement des champs du site");
    },
    onSettled: () => {
      if (queryClient.isMutating({ mutationKey: ["laboratories", "activeFieldsOverrides"] }) === 1) queryClient.invalidateQueries({ queryKey: ["laboratories"] });
    },
  });

  const onFieldChange = (category, key, value) => {
    if (scopeLaboratory) {
      return overridesMutation.mutate({ laboratoryId: scopeLaboratory._id, activeFieldsOverrides: { ...overrides, [category]: { ...overrides[category], [key]: value } } });
    }
    activeFieldsMutation.mutate({ ...setting.activeFields, [category]: { ...setting.activeFields[category], [key]: value } });
  };

  const onInheritField = (category, key) => {
    const categoryOverrides = Object.fromEntries(Object.entries(overrides[category]).filter(([overriddenKey]) => overriddenKey !== key));
    const activeFieldsOverrides = { ...overrides, [category]: categoryOverrides };
    if (Object.keys(categoryOverrides).length === 0) delete activeFieldsOverrides[category];
    overridesMutation.mutate({ laboratoryId: scopeLaboratory._id, activeFieldsOverrides });
  };

  return (
    <div>
      <div className="mb-8">
        <h2 className="text-2xl font-semibold tracking-tight text-gray-900">Champs visibles</h2>
        <p className="mt-1.5 max-w-[64ch] text-sm text-gray-500">
          Choisissez les champs affichés dans un dossier et ceux qui sont obligatoires. Un champ « Requis forçable » est signalé comme requis, mais le dossier peut être validé
          après confirmation s'il reste vide.
        </p>
      </div>

      {laboratories.length > 0 && (
        <div className="sticky top-2 z-10 mb-3 flex flex-wrap items-center gap-x-3 gap-y-2 rounded-xl bg-white/70 p-2 pr-3 shadow-[0_1px_2px_rgba(0,0,0,0.05)] ring-1 ring-gray-950/[0.06] backdrop-blur-xl backdrop-saturate-150">
          <div className="relative shrink-0">
            <LuBuilding2 className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-gray-400" />
            <select
              name="scopeLaboratory"
              aria-label="Site"
              value={scopeLaboratoryId}
              onChange={(e) => setScopeLaboratoryId(e.target.value)}
              className="h-8 max-w-64 cursor-pointer appearance-none rounded-md bg-white pl-8 pr-8 text-[13px] font-medium text-gray-700 shadow-sm outline-none ring-1 ring-gray-950/10 transition-colors hover:bg-gray-50 focus-visible:ring-2 focus-visible:ring-primary-500"
            >
              <option value="">Tous les sites</option>
              {laboratories.map((laboratory) => (
                <option key={laboratory._id} value={laboratory._id}>
                  {laboratory.name}
                  {countOverrides(laboratory) > 0 ? ` (${countOverrides(laboratory)})` : ""}
                </option>
              ))}
            </select>
            <LuChevronsUpDown className="pointer-events-none absolute right-2.5 top-1/2 size-3.5 -translate-y-1/2 text-gray-400" />
          </div>
          <p className="min-w-0 flex-1 basis-60 text-[13px] text-gray-500">
            {!scopeLaboratory && "Configuration commune. Choisissez un site pour lui donner des règles spécifiques."}
            {scopeLaboratory && overridesCount === 0 && "Ce site suit la configuration commune. Modifiez un champ pour le rendre spécifique."}
            {scopeLaboratory &&
              overridesCount > 0 &&
              `${overridesCount} champ${overridesCount > 1 ? "s" : ""} spécifique${overridesCount > 1 ? "s" : ""}, les autres suivent la configuration commune.`}
          </p>
          {overridesCount > 0 && (
            <button
              type="button"
              onClick={() => overridesMutation.mutate({ laboratoryId: scopeLaboratory._id, activeFieldsOverrides: {} })}
              className="h-8 shrink-0 rounded-md px-2.5 text-[13px] font-medium text-gray-700 transition-colors hover:bg-gray-100"
            >
              Tout hériter
            </button>
          )}
        </div>
      )}

      {!setting && (
        <div className="flex justify-center py-16">
          <div className="size-5 animate-spin rounded-full border-2 border-gray-300 border-t-primary-500" />
        </div>
      )}

      {setting && (
        <div className="flex flex-col gap-4">
          {FIELD_GROUPS.map((group) => (
            <div key={group.label} className="overflow-hidden rounded-xl bg-white shadow-[0_1px_2px_rgba(0,0,0,0.05)] ring-1 ring-gray-950/[0.06]">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-100 bg-gray-50/70">
                    <th scope="col" className="px-4 py-2.5 text-left text-[13px] font-medium text-gray-900">
                      {group.label}
                    </th>
                    {FIELD_STATES.map((state) => (
                      <th key={state.value} scope="col" className="w-20 py-2.5 text-center text-xs font-medium text-gray-500 lg:w-[6.5rem] lg:whitespace-nowrap">
                        {state.label}
                      </th>
                    ))}
                    <th className="w-2" />
                  </tr>
                </thead>
                {group.blocks.map((block) => (
                  <tbody key={block.label || block.category} className="divide-y divide-gray-100 border-t border-gray-100">
                    {block.label && (
                      <tr>
                        <th scope="colgroup" colSpan={6} className="px-4 pb-1.5 pt-3 text-left text-xs font-medium text-gray-500">
                          {block.label}
                        </th>
                      </tr>
                    )}
                    {block.fields.map((field) => {
                      const override = overrides[block.category]?.[field.key];
                      const value = override ?? setting.activeFields[block.category][field.key];
                      const isHiddenWithAddress = field.isPartOfAddress && (overrides.patient?.address ?? setting.activeFields.patient.address) === FIELD_STATE.DISABLE;
                      return (
                        <tr key={field.key} className="group/row transition-colors hover:bg-gray-50/70">
                          <th scope="row" className="h-10 px-4 text-left font-normal">
                            <div className="flex flex-wrap items-center gap-x-2 text-[13px]">
                              <span className={value === FIELD_STATE.DISABLE || isHiddenWithAddress ? "text-gray-400" : "text-gray-900"}>{field.label}</span>
                              {override !== undefined && (
                                <>
                                  <span className="chip border-primary-200 bg-primary-50 text-primary-700">Spécifique</span>
                                  <button
                                    type="button"
                                    onClick={() => onInheritField(block.category, field.key)}
                                    title="Revenir à la configuration de tous les sites"
                                    aria-label={`${field.label} : revenir à la configuration de tous les sites`}
                                    className="grid size-6 place-items-center rounded-md text-gray-400 transition-colors hover:bg-gray-200/70 hover:text-gray-700"
                                  >
                                    <LuUndo2 className="size-3.5" />
                                  </button>
                                </>
                              )}
                            </div>
                          </th>
                          {isHiddenWithAddress && (
                            <td colSpan={4} className="text-center text-xs text-gray-400">
                              Masqué avec l'adresse
                            </td>
                          )}
                          {!isHiddenWithAddress &&
                            FIELD_STATES.map((state) =>
                              field.isPartOfAddress && state.value === FIELD_STATE.DISABLE ? (
                                <td key={state.value} />
                              ) : (
                                <td key={state.value} className="p-0">
                                  <label className="relative flex h-10 items-center justify-center">
                                    <input
                                      type="radio"
                                      name={`${block.category}.${field.key}`}
                                      aria-label={`${field.label} : ${state.label}`}
                                      checked={value === state.value}
                                      onChange={() => onFieldChange(block.category, field.key, state.value)}
                                      className="peer absolute inset-0 size-full cursor-pointer appearance-none focus:outline-none"
                                    />
                                    <span
                                      className={`pointer-events-none grid size-[18px] place-items-center rounded-full text-white transition-all duration-150 peer-focus-visible:ring-2 peer-focus-visible:ring-primary-500 peer-focus-visible:ring-offset-2 ${
                                        value === state.value
                                          ? override !== undefined
                                            ? "bg-primary-500"
                                            : scopeLaboratory
                                              ? "bg-gray-300"
                                              : "bg-gray-900"
                                          : "scale-[0.3] bg-gray-200 group-hover/row:scale-100 group-hover/row:bg-white group-hover/row:ring-1 group-hover/row:ring-inset group-hover/row:ring-gray-300 peer-hover:!bg-gray-100 peer-hover:!ring-gray-400 [@media(hover:none)]:scale-100 [@media(hover:none)]:bg-white [@media(hover:none)]:ring-1 [@media(hover:none)]:ring-inset [@media(hover:none)]:ring-gray-300"
                                      }`}
                                    >
                                      {value === state.value && <LuCheck className="size-3" strokeWidth={3} />}
                                    </span>
                                  </label>
                                </td>
                              ),
                            )}
                          <td />
                        </tr>
                      );
                    })}
                  </tbody>
                ))}
              </table>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default VisibleFieldsSection;
